/**
 * Shared executor for the two surfaces' BULK state verbs (#710).
 *
 * The 8 non-destructive bulk verbs — markRead, markUnread, archive,
 * unarchive, markImportant, unmarkImportant, snooze, unsnooze — are
 * byte-identical in PersonalInboxSurface and UnifiedInboxSurface: the same
 * direction payload, the same `settleBulk` toast handling, the same
 * snooze-preset resolution. This module is that identity, so a fix to one
 * verb lands in both inboxes at once.
 *
 * What deliberately stays in each surface: the DESTRUCTIVE bulk verbs
 * (delete/restore) and the predicate-mode guard around them. The two lanes
 * dispatch those differently — Personal confirms once and sends one move,
 * Unified fans out per message through the Trash hooks — and flattening that
 * difference into a shared branch is exactly how the two inboxes drifted
 * before #710.
 */
import { toast } from 'sonner';
import { resolveSnoozePreset } from '../../hooks/useMailState';
import { resolveDisplayZone } from '../../lib/timezone';

/**
 * `true` when `key` was a state verb and the mutation was dispatched;
 * `false` when the caller must handle the key itself (destructive verbs,
 * unknown keys). No return value means "not mine", never "failed" — the
 * mutation's own callbacks report the outcome.
 */
export function dispatchBulkStateAction(key, { target, mutations, user, clearSelection }) {
    const { readMail, archiveMail, importantMail, snoozeMail } = mutations;
    const settleBulk = (verb) => ({
        onSuccess: (res) => {
            clearSelection();
            const modified = res?.data?.modified;
            if (modified === 0) {
                toast.warning('Nothing changed — the message may have moved already');
                return;
            }
            toast.success(verb);
        },
        onError: (err) =>
            toast.error(err?.response?.data?.detail || `Could not ${verb.toLowerCase()}`),
    });

    switch (key) {
        case 'markRead':
            readMail.mutate({ target, read: true }, settleBulk('Marked read'));
            return true;
        case 'markUnread':
            readMail.mutate({ target, read: false }, settleBulk('Marked unread'));
            return true;
        case 'archive':
            archiveMail.mutate({ target, archived: true }, settleBulk('Archived'));
            return true;
        case 'unarchive':
            archiveMail.mutate({ target, archived: false }, settleBulk('Moved to Inbox'));
            return true;
        case 'markImportant':
            importantMail.mutate(
                { target, important: true },
                settleBulk('Marked as important'),
            );
            return true;
        case 'unmarkImportant':
            importantMail.mutate(
                { target, important: false },
                settleBulk('Removed from important'),
            );
            return true;
        case 'snooze': {
            const until = resolveSnoozePreset(
                'tomorrow',
                new Date(),
                resolveDisplayZone({ user }),
            );
            snoozeMail.mutate(
                { target, until: until ? until.toISOString() : null },
                settleBulk('Snoozed'),
            );
            return true;
        }
        case 'unsnooze':
            snoozeMail.mutate({ target, until: null }, settleBulk('Unsnoozed'));
            return true;
        default:
            return false;
    }
}
